// v-focus-trap — makes an element behave as an accessible modal dialog surface.
//
// On insert: remembers the currently-focused element, moves focus into the dialog, and traps
// Tab / Shift+Tab within it. On removal: restores focus to the previously-focused element.
//
// Esc handling is OPTIONAL: pass a function as the binding value and Esc will invoke it
// (`v-focus-trap="close"`). Omit the value for dialogs that already handle Esc themselves
// (e.g. ui/Modal.vue via closeOnEscape) to avoid double-closing — trap + return-focus still apply.
//
// Usage:
//   <div v-focus-trap role="dialog" aria-modal="true" aria-label="...">        (Esc handled elsewhere)
//   <div v-focus-trap="closeDrawer" role="dialog" aria-modal="true" ...>       (directive handles Esc)

const FOCUSABLE = [
  'a[href]', 'area[href]', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', 'button:not([disabled])', 'iframe', 'object', 'embed',
  '[tabindex]:not([tabindex="-1"])', '[contenteditable]'
].join(',')

function focusableNodes(el) {
  return Array.from(el.querySelectorAll(FOCUSABLE))
    .filter(n => n.offsetWidth > 0 || n.offsetHeight > 0 || n === document.activeElement)
}

export default {
  inserted(el, binding) {
    const state = {
      prev: document.activeElement,
      onEsc: typeof binding.value === 'function' ? binding.value : null
    }
    state.keydown = function (e) {
      if (e.key === 'Escape' && state.onEsc) {
        e.stopPropagation()
        state.onEsc(e)
        return
      }
      if (e.key !== 'Tab') return
      const nodes = focusableNodes(el)
      if (nodes.length === 0) { e.preventDefault(); el.focus(); return }
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement
      if (e.shiftKey && (active === first || !el.contains(active))) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && (active === last || !el.contains(active))) {
        e.preventDefault(); first.focus()
      }
    }
    el.__focusTrap__ = state
    el.addEventListener('keydown', state.keydown, true)

    // Move focus into the dialog once it is rendered/visible.
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1')
    const target = focusableNodes(el)[0] || el
    const raf = (typeof requestAnimationFrame === 'function')
      ? requestAnimationFrame
      : (cb) => setTimeout(cb, 0)
    raf(() => { try { target.focus() } catch (_) { /* non-fatal */ } })
  },
  componentUpdated(el, binding) {
    if (el.__focusTrap__ && typeof binding.value === 'function') {
      el.__focusTrap__.onEsc = binding.value
    }
  },
  unbind(el) {
    const state = el.__focusTrap__
    if (!state) return
    el.removeEventListener('keydown', state.keydown, true)
    if (state.prev && typeof state.prev.focus === 'function') {
      try { state.prev.focus() } catch (_) { /* non-fatal */ }
    }
    delete el.__focusTrap__
  }
}
